import { useCallback } from 'react'
import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { lightImpactVibrate } from '../../../../../../utils/appBridge'
import { LumpsumRecommendationActionType } from '../../api/common.types'
import { PlansPlanMode } from '../../api/types'
import {
  FundMissedGainsInfoBottomSheet,
  type FundMissedGainsInfoParams
} from '../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { useLumpsumPlansContext } from '../../plans-list/LumpsumPlansProvider'
import { resolvePlanTransactionCta } from '../../../execution/fundExecutionHelpers'
import { AllocationBuyPlanRecommendationContainer } from '../allocation/buy/AllocationBuyPlanRecommendationContainer'
import { AllocationSellPlanRecommendationContainer } from '../allocation/sell/AllocationSellPlanRecommendationContainer'
import { RebalanceBuyPlanRecommendationContainer } from '../rebalance/buy/RebalanceBuyPlanRecommendationContainer'
import { RebalanceSellPlanRecommendationContainer } from '../rebalance/sell/RebalanceSellPlanRecommendationContainer'
import { IdealPlanRecommendationContainer } from '../ideal/IdealPlanRecommendationContainer'
import {
  isPlanRecommendationBottomSheetBucket,
  resolveRecommendationAction,
  type IdealPlanRecommendationOpenParams,
  type PlanRecommendationOpenParams
} from './planRecommendationOpen'

export function useOpenPlanRecommendationBottomSheet() {
  const { openBottomSheet, closeBottomSheet, openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const { planMode, allocationPlansData, rebalancePlansData, idealData } = useLumpsumPlansContext()

  const openFundMissedGainsInfoNestedBottomSheet = useCallback((params: FundMissedGainsInfoParams) => {
    openNestedBottomSheet(
      <FundMissedGainsInfoBottomSheet
        onDismiss={closeNestedBottomSheet}
        missedGains={params.missedGains}
        currentValue={params.currentValue}
        planSnapshotDate={params.planSnapshotDate}
      />
    )
  }, [closeNestedBottomSheet, openNestedBottomSheet])

  const openPlanRecommendationBottomSheet = useCallback(
    (input: PlanRecommendationOpenParams) => {
      const isin = input.isin ?? ''
      if (!isin || !isPlanRecommendationBottomSheetBucket(input.bucketType)) {
        return
      }

      const resolvedPlanMode = input.planMode ?? planMode
      const recommendationAction = resolveRecommendationAction(
        input.bucketType,
        input.recommendationAction
      )
      if (recommendationAction == null) {
        return
      }

      const transactionCta = resolvePlanTransactionCta({
        planMode: resolvedPlanMode,
        allocationPlansData,
        rebalancePlansData,
        idealData
      })

      const containerProps = {
        onDismiss: closeBottomSheet,
        isin,
        onMissedGainsInfoClick: openFundMissedGainsInfoNestedBottomSheet,
        transactionCta
      }

      lightImpactVibrate()
      if (resolvedPlanMode === PlansPlanMode.REBALANCE) {
        if (recommendationAction === LumpsumRecommendationActionType.BUY) {
          openBottomSheet(<RebalanceBuyPlanRecommendationContainer {...containerProps} />)
          return
        }
        openBottomSheet(<RebalanceSellPlanRecommendationContainer {...containerProps} />)
        return
      }

      if (resolvedPlanMode === PlansPlanMode.ALLOCATION) {
        if (recommendationAction === LumpsumRecommendationActionType.BUY) {
          openBottomSheet(<AllocationBuyPlanRecommendationContainer {...containerProps} />)
          return
        }
        openBottomSheet(<AllocationSellPlanRecommendationContainer {...containerProps} />)
      }
    },
    [closeBottomSheet, openBottomSheet, openFundMissedGainsInfoNestedBottomSheet, planMode, allocationPlansData, rebalancePlansData, idealData]
  )

  const openIdealPlanRecommendationBottomSheet = useCallback(
    (input: IdealPlanRecommendationOpenParams) => {
      const isin = input.isin ?? ''
      if (!isin) {
        return
      }

      lightImpactVibrate()
      openBottomSheet(
        <IdealPlanRecommendationContainer
          onDismiss={closeBottomSheet}
          isin={isin}
          bucketType={input.bucketType}
        />
      )
    },
    [closeBottomSheet, openBottomSheet]
  )

  return {
    openPlanRecommendationBottomSheet,
    openIdealPlanRecommendationBottomSheet,
    openFundMissedGainsInfoNestedBottomSheet
  }
}
